import PersonIcon from '@mui/icons-material/Person';
import { Autocomplete, Avatar, Box, InputAdornment, Stack, TextField } from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import useCatchError from 'utils/axios/useCatchError';
import getInitials from 'utils/getInitials';

const DEFAULT_OPTIONS_PARAMS = {};

/**
 * Input with autocomplete that provide options loading via React Query.
 * {@link ../../docs/components/InputAutocompleteQuery.md Documentation}
 *
 * @param {Object} props Props object
 * @param {String} props.queryKeyPrefix Prefix using by React Query for caching options
 * @param {String} props.label Text above input field
 * @param {Boolean} props.required Flag makes input required (default: `false`)
 * @param {Boolean} props.disabled Flag makes input disabled (default: `false`)
 * @param {Boolean} props.multiple Flag makes field as disabled (default: `false`)
 * @param {Boolean} props.grouped Flag makes field grouped (default: `false`)
 * @param {Boolean} props.error Flag turns input in error state (default: `false`)
 * @param {Number} props.inputDelay Delay between input and request options (default: `300`)
 * @param {String} props.placeholder Text displayed when input focused
 * @param {String} props.loadingText Text displayed when input in loading state
 * @param {String} props.helperText Text displayed under the input
 * @param {String|Number|Array.<String|Number>} props.value Value of the input
 * @param {Function|Array.<{ label: string, value: string|number; caption?: string; captionLines?: string[] }>} props.options Function for fetch options or array of options
 * @param {(value: String|Number|Array.<String|Number>, option: object) => void} props.onChange Callback function that returns selected input value
 * @returns {React.Element}
 */
const InputAutocompleteQuery = ({
  queryKeyPrefix,
  label = 'Select',
  required = false,
  disabled = false,
  multiple = false,
  grouped = false,
  error = false,
  inputDelay = 300,
  placeholder = null,
  loadingText = 'Loading…',
  options: optionsProp,
  optionsParams = DEFAULT_OPTIONS_PARAMS,
  helperText,
  value,
  onChange,
  size,
  iconVariant = 'square',
  iconSize = 40,
  clearable = false,
  showInputIcon = false,
  ...props
}) => {
  // State

  const [inputValue, setInputValue] = useState('');
  const [opened, setOpened] = useState(false);
  const [cachedOptions, setCachedOptions] = useState([]);
  const [cachedValue, setCachedValue] = useState(value);

  // Hooks
  const queryClient = useQueryClient();
  const catchError = useCatchError();

  const queryKey = useMemo(
    () => [
      queryKeyPrefix,
      opened ? inputValue : value,
      optionsParams,
      optionsProp,
    ],
    [inputValue, opened, optionsParams, queryKeyPrefix, value, optionsProp],
  );

  const {
    data: options,
    isFetching,
    setData,
  } = useQuery({
    queryFn: async ({ signal }) => {
      if (optionsProp instanceof Function) {
        return optionsProp(value, inputValue, optionsParams, signal);
      }

      return optionsProp;
    },
    queryKey,
    placeholderData: [],
    keepPreviousData: true,
    onError: catchError,
  });

  // Computable

  const autocompleteOptions = useMemo(() => {
    const cachedOptionsValues = cachedOptions.map(
      ({ value: _value }) => _value,
    );

    return [
      ...cachedOptions,
      ...(options?.filter(
        ({ value: _value }) => !cachedOptionsValues.includes(_value),
      ) ?? []),
    ];
  }, [cachedOptions, options]);

  const autocompleteValue = useMemo(() => {
    if (multiple) {
      if (!value) {
        return [];
      }

      if (!(value instanceof Array)) {
        throw new Error(
          'InputAutocompleteQuery in multiple mode requires value to be an array.',
        );
      }

      return autocompleteOptions.filter((_option) =>
        value.includes(_option.value),
      );
    }

    if (!value) {
      return null;
    }

    return (
      autocompleteOptions.find((_option) => _option.value === value) ?? null
    );
  }, [autocompleteOptions, multiple, value]);

  // Handlers & Methods

  const getOptionLabel = useCallback((option) => option.label, []);

  const handleAutocompleteChange = useCallback(
    (event, _value) => {
      if (multiple) {
        onChange(
          _value.map(({ value: itemValue }) => itemValue),
          _value,
        );

        return;
      }

      onChange(_value?.value ?? null, _value);
    },
    [multiple, onChange],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputChange = useCallback(
    debounce((event, _inputValue) => setInputValue(_inputValue), inputDelay),
    [],
  );

  const handleOpen = useCallback(() => {
    setOpened(true);
    setCachedValue(autocompleteValue);
    setCachedOptions(
      options.filter(({ value: _value }) => {
        if (multiple) {
          return value.includes(_value);
        }

        return value === _value;
      }),
    );
  }, [autocompleteValue, multiple, options, value]);

  const handleClose = useCallback(() => {
    setOpened(false);
    setCachedOptions([]);
    setInputValue('');
    setCachedValue(null);
  }, []);

  const groupBy = useMemo(() => {
    if (grouped) {
      return (option) => option.group;
    }

    return null;
  }, [grouped]);

  const renderOption = useCallback(({ key, ..._props }, option) => {
    const rawCaptionLines =
      option.captionLines !== undefined ? option.captionLines : option.caption;

    const isHighlighted = Boolean(option.highlight);

    const captionLines = (
      Array.isArray(rawCaptionLines)
        ? rawCaptionLines
        : rawCaptionLines
          ? [rawCaptionLines]
          : []
    ).filter(Boolean);

    return (
      <Stack
        // eslint-disable-next-line react/jsx-props-no-spreading
        {..._props}
        key={_props.id}
        direction="row"
        component="li"
        gap={2}
        justifyItems="center"
        sx={{
          minWidth: 0,
          ...(isHighlighted
            ? {
                borderLeft: (theme) =>
                  `3px solid ${theme.palette.primary.main}`,
                pl: 1,
              }
            : {}),
        }}
        data-testid="input_autocomplete_query_option"
        data-label={option.label}
        data-value={option.value}
      >
        {(showInputIcon || option.icon !== undefined) && (
          <Avatar
            src={option.icon}
            variant={iconVariant}
            sx={{ width: iconSize, height: iconSize, fontSize: '1rem' }}
          >
            {getInitials(option.label)}
          </Avatar>
        )}
        <Stack direction="column" sx={{ minWidth: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ minWidth: 0 }}
          >
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {option.label}
            </Box>
            {isHighlighted && (
              <Box
                component="span"
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  borderRadius: 999,
                  fontSize: 12,
                  lineHeight: 1,
                  px: 1,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Site
              </Box>
            )}
          </Stack>
          {captionLines.map((line, index) => (
            <Box
              component="span"
              key={index}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
                whiteSpace: 'nowrap',
                color: (theme) =>
                  isHighlighted
                    ? theme.palette.primary.main
                    : theme.palette.grey[400],
              }}
            >
              {line}
            </Box>
          ))}
        </Stack>
      </Stack>
    );
  }, [iconVariant, iconSize, showInputIcon]);

  const renderInput = useCallback(
    (params) => {
      const { InputProps } = params;
      const {
        startAdornment: inputStartAdornment,
        endAdornment: inputEndAdornment,
      } = InputProps;

      let startAdornment = inputStartAdornment;

      if (showInputIcon) {
        const selectedOption = opened ? cachedValue : autocompleteValue;

        startAdornment = (
          <>
            {selectedOption ? (
              <Avatar
                src={selectedOption.icon}
                variant={iconVariant}
                sx={{
                  width: iconSize,
                  height: iconSize,
                  fontSize: '0.75rem',
                  mr: 1,
                  flexShrink: 0,
                }}
              >
                {getInitials(selectedOption.label)}
              </Avatar>
            ) : (
              <InputAdornment position="start">
                <PersonIcon fontSize="small" />
              </InputAdornment>
            )}
            {inputStartAdornment}
          </>
        );
      }

      const inputPlaceholder = showInputIcon
        ? placeholder ?? label
        : isFetching
          ? loadingText
          : placeholder;

      return (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          fullWidth={true}
          variant="outlined"
          label={showInputIcon ? undefined : label}
          error={error}
          helperText={helperText}
          disabled={disabled}
          InputLabelProps={{
            required,
          }}
          inputProps={{
            ...params.inputProps,
            'data-value': JSON.stringify(value),
          }}
          placeholder={inputPlaceholder}
          InputProps={{
            ...InputProps,
            startAdornment,
            endAdornment: inputEndAdornment,
          }}
        />
      );
    },
    [
      autocompleteValue,
      cachedValue,
      disabled,
      error,
      helperText,
      iconSize,
      iconVariant,
      isFetching,
      label,
      loadingText,
      opened,
      placeholder,
      required,
      showInputIcon,
      value,
    ],
  );

  // Effects

  useEffect(() => {
    if (opened) {
      setCachedValue(autocompleteValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!(optionsProp instanceof Function)) {
      queryClient.setQueryData(queryKey, optionsProp);
    }
  }, [optionsProp, queryClient, queryKey]);

  // Render

  return (
    <Autocomplete
      fullWidth={true}
      autoComplete={false}
      autoSelect={false}
      clearOnBlur={true}
      disableClearable={!clearable}
      // eslint-disable-next-line react/destructuring-assignment
      data-testid={props['data-testid'] ?? 'input_autocomplete_query'}
      data-value={JSON.stringify(value)}
      loading={isFetching}
      options={autocompleteOptions}
      multiple={multiple}
      disabled={disabled}
      filterSelectedOptions={multiple}
      value={opened ? cachedValue : autocompleteValue}
      renderInput={renderInput}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      size={size}
      onChange={handleAutocompleteChange}
      onOpen={handleOpen}
      onClose={handleClose}
      onInputChange={handleInputChange}
    />
  );
};

InputAutocompleteQuery.propTypes = {
  queryKeyPrefix: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  grouped: PropTypes.bool,
  error: PropTypes.bool,
  placeholder: PropTypes.string,
  loadingText: PropTypes.string,
  helperText: PropTypes.string,
  // eslint-disable-next-line no-useless-computed-key
  ['data-testid']: PropTypes.string,
  inputDelay: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf([PropTypes.string, PropTypes.number]),
    PropTypes.array,
  ]),
  options: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        caption: PropTypes.string,
        icon: PropTypes.string,
        captionLines: PropTypes.arrayOf(PropTypes.string),
        highlight: PropTypes.bool,
        grouped: PropTypes.bool,
      }),
    ),
  ]).isRequired,
  iconVariant: PropTypes.oneOf(['circular', 'rounded', 'square']),
  iconSize: PropTypes.number,
  showInputIcon: PropTypes.bool,
  clearable: PropTypes.bool,
  optionsParams: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

/**
 * @type {InputAutocompleteQuery}
 */
export default memo(InputAutocompleteQuery);
